// GERADO de backend-senne/src/contracts — NÃO editar; rode scripts/sync-contracts.sh

/**
 * The authentication error codes: one meaning per code, for the backend and the portals (P20EE-105).
 *
 * Before this file the backend (`shared/errors/DetailedError.ts`) and the exam portal (`authErrorHandler.ts`) each kept
 * a table of their own, and they disagreed on almost every number: `AUTH_012` was MFA_REQUIRED here and "E-mail não
 * verificado" there, `AUTH_016` was CODE_EXPIRED here and "Operação não permitida" there. The value is the wire
 * contract. A portal keys its messages by the NAME, so a code added here without a message there fails the portal's
 * typecheck.
 *
 * Numbers are never reused: `AUTH_011` (expired password, removed with spec 025) and `AUTH_019`/`AUTH_020` (held by
 * the portal's old table) stay unassigned.
 */
export const AuthErrorCode = {
    INVALID_CREDENTIALS: 'AUTH_001',
    INVALID_IDENTIFIER: 'AUTH_002',
    INVALID_CODE: 'AUTH_003',
    INVALID_TOKEN: 'AUTH_004',
    EXPIRED_TOKEN: 'AUTH_005',
    INVALID_REFRESH_TOKEN: 'AUTH_006',
    EXPIRED_REFRESH_TOKEN: 'AUTH_007',

    USER_NOT_FOUND: 'AUTH_008',
    USER_INACTIVE: 'AUTH_009',
    USER_LOCKED: 'AUTH_010',
    USER_BLOCKED: 'AUTH_022',
    USER_PENDING: 'AUTH_023',
    MFA_REQUIRED: 'AUTH_012',

    SESSION_NOT_FOUND: 'AUTH_013',
    SESSION_REVOKED: 'AUTH_014',
    SESSION_COMPROMISED: 'AUTH_015',
    REFRESH_IN_PROGRESS: 'AUTH_021',

    CODE_EXPIRED: 'AUTH_016',
    CODE_ALREADY_USED: 'AUTH_017',
    CODE_NOT_FOUND: 'AUTH_018',

    INTERNAL_ERROR: 'AUTH_999',
    DATABASE_ERROR: 'AUTH_998',
    EXTERNAL_SERVICE_ERROR: 'AUTH_997',
} as const

export type AuthErrorCodeName = keyof typeof AuthErrorCode
export type AuthErrorCodeType = (typeof AuthErrorCode)[AuthErrorCodeName]

const CODE_VALUES: ReadonlySet<string> = new Set(Object.values(AuthErrorCode))

/** Whether a value is one of the codes above, such as the `code` of an error the backend raised. */
export function isAuthErrorCode(value: unknown): value is AuthErrorCodeType {
    return typeof value === 'string' && CODE_VALUES.has(value)
}

/** The name of a code, for a portal that keys its messages by name; `undefined` for any other value. */
export function authErrorCodeName(
    value: unknown
): AuthErrorCodeName | undefined {
    if (!isAuthErrorCode(value)) return undefined
    return (Object.keys(AuthErrorCode) as AuthErrorCodeName[]).find(
        (name) => AuthErrorCode[name] === value
    )
}
